import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { PersonConfigFileType } from '@enums/person-config-file-type.enum';
import { UserCapacity } from '@enums/user-capacity.enum';
import { UserType } from '@enums/user-type.enum';
import { EmailFormControl } from '@forms/email-form-control';
import { PersonConfigFileForm } from '@forms/person-config-file-form';
import { AddressForm } from '@forms/user/address-form';
import { DirectorForm } from '@forms/user/director-form';
import { LegalInformationForm } from '@forms/user/legal-information-form';
import { OwnershipForm } from '@forms/user/ownership-form';
import { ArithmeticHelper } from '@helpers/arithmetic.helper';
import { DateHelper } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { PhoneHelper } from '@helpers/phone.helper';
import { ValidatorHelper } from '@helpers/validators.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { PersonConfig } from '@models/person-config';
import { PersonConfigFile } from '@models/person-config-file';
import { PieceFile } from '@models/piece-file';
import { User } from '@models/user';

export type UserFormType = {
  id: FormControl<number | undefined>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  email: FormControl<string | null>;
  emails: FormArray<EmailFormControl>;
  phone: FormControl<string | null>;
  capacity: FormControl<UserCapacity | undefined>;
  civility: FormControl<string | undefined>;
  address: AddressForm;
  ownership: OwnershipForm;
  legalInformation: LegalInformationForm;
  director: DirectorForm;
  files: FormArray<PersonConfigFileForm>;
  filesToDelete: FormArray<PersonConfigFileForm>;
};

export class UserForm extends FormGroup<UserFormType> {
  get id(): FormControl<number | undefined> {
    return this.controls.id;
  }

  get firstName(): FormControl<string | null> {
    return this.controls.firstName;
  }

  get firstNameErrorMessage(): string {
    return this.firstName.touched && this.firstName.hasError('required') ? 'Prénom requis' : null;
  }

  get lastName(): FormControl<string | null> {
    return this.controls.lastName;
  }

  get lastNameErrorMessage(): string {
    return this.lastName.touched && this.lastName.hasError('required') ? 'Nom requis' : null;
  }

  get email(): FormControl<string | null> {
    return this.controls.email;
  }

  get emails(): FormArray<EmailFormControl> {
    return this.controls.emails;
  }

  get emailErrorMessage(): string {
    if (!this.email.touched) {
      return null;
    }

    if (this.email.hasError('required')) {
      return 'Email requis';
    }

    if (this.email.hasError('email')) {
      return 'Email invalide';
    }

    return null;
  }

  get phone(): FormControl<string | null> {
    return this.controls.phone;
  }

  get phoneErrorMessage(): string {
    if (!this.phone.touched) {
      return null;
    }

    if (this.phone.hasError('required')) {
      return 'Téléphone requis';
    }

    if (this.phone.hasError('invalidPhoneNumber')) {
      return 'Téléphone invalide';
    }

    return null;
  }

  get capacity(): FormControl<UserCapacity | undefined> {
    return this.controls.capacity;
  }

  get capacityErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.capacity, 'Qualité requise');
  }

  get civility(): FormControl<string | undefined> {
    return this.controls.civility;
  }

  get address(): AddressForm {
    return this.controls.address;
  }

  get ownership(): OwnershipForm {
    return this.controls.ownership;
  }

  get legalInformation(): LegalInformationForm {
    return this.controls.legalInformation;
  }

  get director(): DirectorForm {
    return this.controls.director;
  }

  get files(): FormArray<PersonConfigFileForm> {
    return this.controls.files;
  }

  get filesToDelete(): FormArray<PersonConfigFileForm> {
    return this.controls.filesToDelete;
  }

  constructor(
    emailValidators = [ValidatorHelper.emailValidator()],
    phoneValidators = [PhoneHelper.phoneNumberValidator()]
  ) {
    super({
      id: new FormControl<number | undefined>(undefined),
      firstName: new FormControl<string | null>(null, Validators.required),
      lastName: new FormControl<string | null>(null, Validators.required),
      email: new FormControl<string | null>(null, emailValidators),
      emails: new FormArray<EmailFormControl>([]),
      phone: new FormControl<string | null>(null, phoneValidators),
      capacity: new FormControl<UserCapacity | undefined>(undefined),
      civility: new FormControl<string | undefined>(undefined),
      address: new AddressForm(),
      ownership: new OwnershipForm(),
      legalInformation: new LegalInformationForm(),
      director: new DirectorForm(),
      files: new FormArray<PersonConfigFileForm>([]),
      filesToDelete: new FormArray<PersonConfigFileForm>([])
    });
  }

  fromUser(user: User): void {
    this.patchValue({
      id: user.id,
      capacity: user.capacity,
      civility: user.civility,
      lastName: user.lastName,
      firstName: user.firstName,
      phone: user.phone && PhoneHelper.isValid(user.phone) ? PhoneHelper.format(user.phone) : user.phone,
      email: user.email,
      address: {
        mailingStreet: user.mailingStreet,
        mailingCity: user.mailingCity,
        mailingPostalCode: user.mailingPostalCode,
        mailingCountry: user.mailingCountry,
        mailingCityInseeCode: user.mailingCityInseeCode
      },
      ownership: {
        capitalContribution: user.capitalContribution,
        sharesNumber: user.sharesNumber,
        entryDate: user.entryDate,
        exitDate: user.exitDate,
        sharesBareOwnershipNumber: user.sharesBareOwnershipNumber,
        sharesUsufructNumber: user.sharesUsufructNumber
      },
      legalInformation: {
        birthDate: user.birthDate,
        birthPlace: user.birthPlace,
        birthDepartment: user.birthDepartment,
        birthCountry: user.birthCountry,
        birthCityInseeCode: user.birthCityInseeCode,
        nationality: user.nationality,
        maritalStatus: user.maritalStatus,
        taxNumber: user.taxNumber,
        socialSecurityNumber: user.socialSecurityNumber
      },
      director: {
        socialRegime: user.socialRegime,
        startJobDate: user.startJobDate,
        endJobDate: user.endJobDate,
        acre: user.acre,
        jobCenterRegistrationDate: user.jobCenterRegistrationDate,
        jobCenterRegistrationEndDate: user.jobCenterRegistrationEndDate
      }
    });

    user.personConfig?.emails.forEach((email: string) => this.addEmail(email));
    user.personConfig?.personConfigFiles.forEach((file: PersonConfigFile) => this.addFile(file));
  }

  toUser(withPersonConfig = true): User {
    const user = new User();
    user.type = UserType.user;
    user.id = this.id.value;
    user.firstName = this.firstName.value;
    user.lastName = this.lastName.value;
    user.email = this.email.value;
    user.phone = this.phone.value;
    user.civility = this.civility.value;
    user.capacity = this.capacity.value;
    user.capitalContribution = this.ownership.capitalContribution.value;
    user.sharesNumber = ArithmeticHelper.safeParseInt(this.ownership.sharesNumber.value);
    user.sharesBareOwnershipNumber = ArithmeticHelper.safeParseInt(this.ownership.sharesBareOwnershipNumber.value);
    user.sharesUsufructNumber = ArithmeticHelper.safeParseInt(this.ownership.sharesUsufructNumber.value);
    user.entryDate = this.ownership.entryDate.value ? DateHelper.format(this.ownership.entryDate.value) : null;
    user.exitDate = this.ownership.exitDate.value ? DateHelper.format(this.ownership.exitDate.value) : null;
    user.mailingStreet = this.address.mailingStreet.value;
    user.mailingCity = this.address.mailingCity.value;
    user.mailingPostalCode = this.address.mailingPostalCode.value;
    user.mailingCountry = this.address.mailingCountry.value;
    user.mailingCityInseeCode = this.address.mailingCityInseeCode.value;
    user.birthDate = this.legalInformation.birthDate.value
      ? DateHelper.format(this.legalInformation.birthDate.value)
      : null;
    user.birthPlace = this.legalInformation.birthPlace.value;
    user.birthDepartment = this.legalInformation.birthDepartment.value;
    user.birthCityInseeCode = this.legalInformation.birthCityInseeCode.value;
    user.birthCountry = this.legalInformation.birthCountry.value;
    user.nationality = this.legalInformation.nationality.value;
    user.maritalStatus = this.legalInformation.maritalStatus.value;
    user.taxNumber = this.legalInformation.taxNumber.value;
    user.socialSecurityNumber = this.legalInformation.socialSecurityNumber.value;
    user.socialRegime = this.director.socialRegime.value;
    user.startJobDate = this.director.startJobDate.value ? DateHelper.format(this.director.startJobDate.value) : null;
    user.endJobDate = this.director.endJobDate.value ? DateHelper.format(this.director.endJobDate.value) : null;
    user.acre = this.director.acre.value;
    if (withPersonConfig) {
      user.personConfig = new PersonConfig(
        this.emails.controls.map((control: EmailFormControl) => control.value),
        this.files.controls.map((control: PersonConfigFileForm) => control.toFile())
      );
    }
    user.jobCenterRegistrationDate = this.director.jobCenterRegistrationDate.value
      ? DateHelper.format(this.director.jobCenterRegistrationDate.value)
      : null;
    user.jobCenterRegistrationEndDate = this.director.jobCenterRegistrationEndDate.value
      ? DateHelper.format(this.director.jobCenterRegistrationEndDate.value)
      : null;

    return user;
  }

  addEmail(email = null): void {
    const emailControl: EmailFormControl = new EmailFormControl(email, [Validators.required]);
    this.emails.push(emailControl);
  }

  removeEmail(index: number): void {
    this.emails.removeAt(index);
  }

  addFile(file: PersonConfigFile): void {
    const fileControl: PersonConfigFileForm = new PersonConfigFileForm();
    fileControl.fromFile(file);
    this.files.push(fileControl);
  }

  addNewFiles(files: PieceFile[]): void {
    files.forEach((file: PieceFile) => {
      const fileControl: PersonConfigFileForm = new PersonConfigFileForm();
      fileControl.name.setValue(file.name);
      fileControl.type.setValue(PersonConfigFileType.identityPiece);
      fileControl.content.setValue(file.file);
      this.files.push(fileControl);
    });
  }

  removeFile(index: number): void {
    const fileForm: PersonConfigFileForm = this.files.controls.find((_, fileIndex: number) => index === fileIndex);
    if (fileForm?.id.value) {
      this.filesToDelete.push(fileForm);
    }
    this.files.removeAt(index);
  }

  getFilesToUpload(): PersonConfigFile[] {
    return this.files.controls
      .filter((fileForm: PersonConfigFileForm) => !fileForm.id.value && fileForm.content.value)
      .map((fileForm: PersonConfigFileForm) => fileForm.toFile());
  }

  getFilesToDelete(): number[] {
    return this.filesToDelete.controls.map((fileForm: PersonConfigFileForm) => fileForm.id.value);
  }
}
